import { IngestionFolderValidationErrorReviewFragment } from '@client/graphql/__generated__/types';
import { getMissingDocumentMessage } from './ingestionDocuments.utils';

export type FolderValidation = {
  errorCount: number;
  validationResults?: Array<{
    documentCategory?: string;
    documentForm?: string;
    documentName?: string;
    requiredDocument?: {
      isMissing?: boolean;
      isRequired?: boolean;
      message?: string;
    };
    ruleResults?: Record<string, unknown>[];
  }>;
};

export type MissingDocumentItem = {
  mainLabel: string;
  subLabel?: string;
  documentCategory?: string;
  documentForm?: string;
  documentName?: string;
};

export type OfferRuleItem = {
  sourceId: string;
  message: string;
  documentCategory?: string;
  documentForm?: string;
  documentName?: string;
  ruleUuid?: string | null;
};

type FolderRuleResult = {
  message?: string;
  hasError?: boolean;
  hasWarning?: boolean;
  passedRequirement?: boolean;
  isEnabled?: boolean;
  isDisabled?: boolean;
  output?: string | boolean;
  rule?: {
    uuid?: string;
    ruleType?: string;
    message?: string;
  };
  inputValues?: {
    value: string;
    fieldKey: string;
    variableSource?: string;
  }[];
};

/**
 * Normalize optional GraphQL-ish string fields so "undefined" and "null" compare the same.
 */
const normalizeValue = (value?: string | null) => value ?? null;

/**
 * Extract the required-but-missing documents from a folder validation payload.
 * Preserves the original document metadata so the UI can label the issue accurately.
 */
export const getMissingDocumentsFromFolderValidation = (
  folderValidation?: FolderValidation
): MissingDocumentItem[] => {
  if (!folderValidation?.validationResults) {
    return [];
  }

  return folderValidation.validationResults
    .filter(
      (result) =>
        result.requiredDocument?.isMissing &&
        result.requiredDocument?.isRequired
    )
    .map((result) => {
      const message = getMissingDocumentMessage(result);

      return message
        ? {
            ...message,
            documentCategory: result.documentCategory,
            documentForm: result.documentForm,
            documentName: result.documentName,
          }
        : null;
    })
    .filter(Boolean) as MissingDocumentItem[];
};

/**
 * Check if a missing document issue was explicitly hidden by the reviewer.
 * Comparison is done on normalized document identifiers to survive null/undefined noise.
 */
export const isMissingDocumentHidden = (
  document: MissingDocumentItem,
  hiddenIssues?: IngestionFolderValidationErrorReviewFragment[]
) => {
  if (!hiddenIssues) {
    return false;
  }

  return hiddenIssues.some(
    (review) =>
      normalizeValue(review.documentCategory) ===
        normalizeValue(document.documentCategory) &&
      normalizeValue(review.documentForm) ===
        normalizeValue(document.documentForm) &&
      normalizeValue(review.documentName) ===
        normalizeValue(document.documentName)
  );
};

/**
 * Flatten folder validation rule failures into offer-rule issue items consumable by the UI.
 * Only rules marked as errors surface here; warnings stay quiet.
 */
export const getOfferRuleItemsFromFolderValidation = (
  folderValidation?: FolderValidation
): OfferRuleItem[] => {
  if (!folderValidation?.validationResults) {
    return [];
  }

  const items: OfferRuleItem[] = [];

  folderValidation.validationResults.forEach(
    (validationResult, validationIndex) => {
      const ruleResults =
        (validationResult.ruleResults as FolderRuleResult[] | undefined) || [];

      ruleResults
        .filter((ruleResult) => ruleResult?.hasError)
        .forEach((ruleResult, ruleIndex) => {
          const message =
            ruleResult.message ||
            ruleResult.rule?.message ||
            'Offer ingestion rule triggered';

          items.push({
            sourceId: `${
              ruleResult.rule?.uuid ?? 'offer-rule'
            }-${validationIndex}-${ruleIndex}`,
            message,
            documentCategory: validationResult.documentCategory,
            documentForm: validationResult.documentForm,
            documentName: validationResult.documentName,
            ruleUuid: ruleResult.rule?.uuid ?? null,
          });
        });
    }
  );

  return items;
};

/**
 * Determine whether a surfaced offer rule issue was muted already.
 * Prefers rule UUID matches but falls back to message + document tuple comparison.
 */
export const isOfferRuleItemHidden = (
  item: OfferRuleItem,
  hiddenIssues?: IngestionFolderValidationErrorReviewFragment[]
) => {
  if (!hiddenIssues) {
    return false;
  }

  const normalizedRuleUuid = normalizeValue(item.ruleUuid);

  return hiddenIssues.some((issue) => {
    const issueRuleUuid = normalizeValue(issue.ruleUuid);
    if (normalizedRuleUuid !== null && issueRuleUuid === normalizedRuleUuid) {
      return true;
    }

    return (
      normalizeValue(issue.message) === normalizeValue(item.message) &&
      normalizeValue(issue.documentCategory) ===
        normalizeValue(item.documentCategory) &&
      normalizeValue(issue.documentForm) ===
        normalizeValue(item.documentForm) &&
      normalizeValue(issue.documentName) === normalizeValue(item.documentName)
    );
  });
};
